import { z } from 'zod';

/**
 * Who a non-project category is a standing row for. The array is the source of
 * truth and the type is derived from it (CLAUDE.md: no magic strings), the same
 * shape as AUDIT_OBJECT_TYPES.
 *
 * - `everyone`  — every active user's editable timesheets.
 * - `approvers` — approvers' and administrators' only (a role gate; 0031's
 *                 `approver_only = true`).
 * - `leads`     — assigned by leads: a person has it when their individual
 *                 choice says granted, or, with no choice, when any team they
 *                 belong to has the category set to "whole team". Decided per
 *                 person under General activities in Projects & tasks.
 *
 * `@time-tracking/db` types the `admin_categories.visibility` column with its
 * own copy of this union (db depends on nothing in the workspace). The API
 * package, which imports both, proves the two are the same set at compile time —
 * see `CategoryVisibilityUnionsAgree` in `packages/api/src/repositories/categories.ts`.
 */
export const CATEGORY_VISIBILITIES = ['everyone', 'approvers', 'leads'] as const;
export const CategoryVisibility = z.enum(CATEGORY_VISIBILITIES);
export type CategoryVisibility = z.infer<typeof CategoryVisibility>;

// The coverage of one lead-assigned category. Every people count is of
// DISTINCT ACTIVE people (the System actor excluded), the same population the
// audience-impact counts use.
export const AdminCategoryCoverage = z.object({
  // Teams whose "whole team" setting is on for this category.
  wholeTeamTeams: z.number().int().nonnegative(),
  // Every team (the denominator of `wholeTeamTeams`).
  totalTeams: z.number().int().nonnegative(),
  // People with an individual choice of "granted" — added individually by a
  // lead or an administrator, whatever their teams say.
  individuallyGranted: z.number().int().nonnegative(),
  // People the category is granted to now, by the same grant predicate the
  // timesheet seed uses (an individual choice, else any whole-team team).
  peopleWithIt: z.number().int().nonnegative(),
  // Every active person (the denominator of `peopleWithIt`).
  totalPeople: z.number().int().nonnegative(),
});
export type AdminCategoryCoverage = z.infer<typeof AdminCategoryCoverage>;

// A non-project category (a standing time bucket like "Gen Admin" or "Leave").
// Every category row is global — there is no team-scope field. `visibility`
// decides whose timesheets it is a standing row on: by ROLE for `approvers`, by
// PERSON (team settings plus individual choices) for `leads`.
//
// `visibility` is a ROUTING RULE, NOT A CONFIDENTIALITY BOUNDARY. It decides
// which timesheets the category is seeded onto; it decides nothing about who
// may read its name or its hours. This endpoint stays ungated and unfiltered on
// purpose: a retained line — one that already carries hours when the setting is
// changed, or when an ordinary team edit demotes its owner or a lead removes
// the activity — must keep its label, and the label comes from here. The
// accepted consequence is that a contributor's browser can enumerate every
// category name, and their hours reach shared analytics unchanged. Categories
// holding genuinely confidential work are not what this setting is for.
//
// The client must NEVER use this setting to filter rows out of a timesheet. The
// server decides which lines exist; the grid renders whatever it was sent. A
// client-side filter here is the U-07 bug — "lines never disappear"
// (REQUIREMENTS.md) — and it would fire on the commonest case rather than an
// exotic one: demotion is a side effect of an ordinary team edit, so hours
// already logged would vanish from their owner's own sheet.
export const AdminCategoryItem = z.object({
  categoryId: z.string().uuid(),
  name: z.string(),
  // Work time (counts as work) vs non-work (time off, e.g. Leave).
  isWorkTime: z.boolean(),
  // false = deactivated (kept for history, not offered on new timesheets).
  activeFlag: z.boolean(),
  // Cutoff date (YYYY-MM-DD) after which no new hours may be charged, or null.
  lockedAt: z.string().nullable(),
  // Who the category is a standing row for. See the routing-rule note above
  // before reading anything else into it. Replaces 0031's `approverOnly`
  // boolean, which this DTO does not carry: parsing strips it, so no SPA code
  // can read it (the API still emits it for one release — see
  // LegacyAdminCategoryItemWire).
  //
  // Defaulted rather than required so deploy skew degrades correctly: an SPA
  // newer than the API parses a response that predates this field and reads
  // "visible to everyone", which is what an API without the field means for
  // every category it could not mark approver-only. A bare enum would instead
  // throw and blank the whole category catalog, taking every non-project row's
  // label with it.
  visibility: CategoryVisibility.default('everyone'),
  // Who a lead-assigned category reaches right now — the admin table's coverage
  // line ("2 of 4 teams · 3 people · 27 of 40 people"). See
  // AdminCategoryCoverage. Present ONLY when `visibility` is `leads` AND the
  // caller is an administrator: the catalog GET is the ungated one every
  // contributor's timesheet loads, and it should not pay for an aggregate only
  // the admin pane reads. Absent means "not computed for you", never "nobody".
  coverage: AdminCategoryCoverage.optional(),
});
export type AdminCategoryItem = z.infer<typeof AdminCategoryItem>;

// TEMPORARY deploy-skew bridge: what the API actually EMITS for a category,
// for one release — AdminCategoryItem plus the legacy `approverOnly ===
// (visibility === 'approvers')`, so an admin tab still running the
// pre-`visibility` SPA keeps rendering approver-only categories correctly (it
// defaults a missing `approverOnly` to false, which would show every one of
// them as visible to everyone). Server-side only: clients parse with
// AdminCategoryItem, which strips the field, so new code cannot come to depend
// on it. The write schemas below still REFUSE `approverOnly`.
// TODO(follow-up release: drop admin_categories.approver_only): remove, and
// emit AdminCategoryItem directly.
export const LegacyAdminCategoryItemWire = AdminCategoryItem.extend({
  approverOnly: z.boolean(),
});
export type LegacyAdminCategoryItemWire = z.infer<typeof LegacyAdminCategoryItemWire>;

export const AdminCategoriesResponse = z.array(AdminCategoryItem);
export type AdminCategoriesResponse = z.infer<typeof AdminCategoriesResponse>;

// What switching this category to another visibility would do to the people
// it reaches, split by outcome — the evidence the admin's switch-confirm dialog
// is decided on. Requested per target (`?to=` — see
// AdminCategoryAudienceImpactQuery); the current visibility is the server's to
// read, never the client's to assert.
//
// Every count is of DISTINCT active USERS, and the people buckets are disjoint:
// each affected person lands in exactly one of `keeping`, `gaining`, `losing`
// or `retaining`. Between the two losing buckets "retained" wins: somebody with
// hours on one week and an empty row on another is retaining, not losing,
// because the outcome they care about is that nothing they logged goes away.
// "Rows" here are the rows they hold on editable (draft/returned) sheets —
// every such week, not only the current one, because seeding and the sweep act
// on all of them.
//
// "Has it" (today and afterwards) is the timesheet seed's own grant predicate,
// so `keeping` and `gaining` are about access. The two losing buckets are about
// ROWS: they count the people who lose access AND hold such a row, because a
// row is what the sweep takes or keeps. Someone who loses access while holding
// no row on any editable sheet (say, a person who has not opened a timesheet
// since the category appeared) is in neither — nothing on any sheet changes for
// them. That keeps `retaining`/`losing` exactly the numbers the `→ approvers`
// dialog has always quoted.
export const AdminCategoryAudienceImpact = z.object({
  // People who have the category today and still have it afterwards.
  keeping: z.number().int().nonnegative(),
  // People who do not have it today and will get it (the seed adds the row on
  // their next timesheet load).
  gaining: z.number().int().nonnegative(),
  // People who lose it and hold at least one line the sweep spares — it carries
  // time entries or a contributor note. The row is retained immediately,
  // badged, and takes no new hours (U-07 — logged hours never disappear); a
  // decrease still saves.
  retaining: z.number().int().nonnegative(),
  // People who lose it and hold no such line. Their empty, note-free rows are
  // swept at once and the seed stops creating more.
  losing: z.number().int().nonnegative(),
  // People on no team who have the category today and lose ACCESS to it —
  // whether or not they hold a row (unlike `retaining`/`losing`, so this is not
  // a subset of them: it also counts someone with no timesheet row yet). Each
  // person once. Non-zero only for a switch INTO `leads`: start-fresh gives the
  // category to every team's whole team, so people on no team are the ones it
  // leaves out (an administrator can still grant them individually).
  noTeamLosing: z.number().int().nonnegative(),
  // Teams that would start with "whole team" on. Non-zero only for a switch
  // into `leads` (the dialog's "It starts assigned to all {T} teams").
  teamCount: z.number().int().nonnegative(),
  // Stored lead choices (team settings and individual choices) left over from
  // an earlier stint as `leads`, which a switch into `leads` discards — the
  // start-fresh rule. Zero for any other target.
  discardedChoices: z.number().int().nonnegative(),
});
export type AdminCategoryAudienceImpact = z.infer<typeof AdminCategoryAudienceImpact>;

// TEMPORARY deploy-skew bridge: what `GET …/audience-impact` answers when it
// is called with NO `?to=` — which only the pre-`visibility` admin pane does,
// and it only ever asked about a `→ approvers only` flip. The answer is the
// `→ approvers` impact plus the two count names that pane reads, which are the
// same numbers as `retaining` / `losing` (people who lose it and do / do not
// hold a row the sweep spares). A client that sends `?to=` gets the plain
// AdminCategoryAudienceImpact.
// TODO(follow-up release: drop admin_categories.approver_only): remove, and
// make a missing `?to=` a 400 again.
export const LegacyAdminCategoryAudienceImpact = AdminCategoryAudienceImpact.extend({
  retainingContributors: z.number().int().nonnegative(),
  losingContributors: z.number().int().nonnegative(),
});
export type LegacyAdminCategoryAudienceImpact = z.infer<typeof LegacyAdminCategoryAudienceImpact>;

export const AdminCategoryAudienceImpactQuery = z.object({
  to: CategoryVisibility,
});
export type AdminCategoryAudienceImpactQuery = z.infer<typeof AdminCategoryAudienceImpactQuery>;

// `.strict()` on both write schemas: the legacy `approverOnly` field is REFUSED
// (400), not stripped. A stale admin pane that still sends `{ approverOnly:
// false }` would otherwise have its request accepted as a no-op and report
// success, and the only alternative to stripping — mapping it — could flip a
// lead-assigned category to Everyone. A loud refusal is the honest answer to a
// client that predates the three-way setting.
export const CreateAdminCategoryRequest = z
  .object({
    name: z.string().min(1).max(255),
    isWorkTime: z.boolean(),
    // Optional (`visibility?: CategoryVisibility | undefined` under
    // `exactOptionalPropertyTypes`), not defaulted here: omitting it leaves the
    // `admin_categories.visibility` column default — everyone — to decide, so
    // there is exactly one statement of what "unspecified" means rather than a
    // Zod copy that can drift from the column.
    visibility: CategoryVisibility.optional(),
  })
  .strict();
export type CreateAdminCategoryRequest = z.infer<typeof CreateAdminCategoryRequest>;

// Edit fields and/or deactivate/reactivate and/or set-or-clear the lock cutoff
// and/or change who the category is seeded onto (`visibility`).
// All fields optional (partial patch). lockedAt: 'YYYY-MM-DD' to lock, null to unlock.
//
// `.partial()` under `exactOptionalPropertyTypes` gives every field the
// `field?: T | undefined` spelling, so an omitted key and an explicit
// `undefined` are the same "don't touch this" instruction. Send only the keys
// you mean to change: there is no version or If-Match on this PATCH, so a
// caller that round-trips a whole object silently reverts anything a colleague
// changed while its copy was stale.
export const UpdateAdminCategoryRequest = z
  .object({
    name: z.string().min(1).max(255),
    isWorkTime: z.boolean(),
    activeFlag: z.boolean(),
    lockedAt: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'lockedAt must be YYYY-MM-DD')
      .nullable(),
    // See AdminCategoryItem.visibility — a routing rule, not a privacy one.
    visibility: CategoryVisibility,
  })
  .partial()
  .strict();
export type UpdateAdminCategoryRequest = z.infer<typeof UpdateAdminCategoryRequest>;
